const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/** e.g. 1536000 -> "1.5 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    FILE_SIZE_UNITS.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${FILE_SIZE_UNITS[exponent]}`;
}

/** Initials for an avatar fallback, e.g. "Jane Doe" -> "JD". */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Title-cases a person's name for DISPLAY: the first letter of each word uppercased, the rest
 * lowercased — "chaitanya" -> "Chaitanya", "chaitanya KRISHNA" -> "Chaitanya Krishna". Word breaks
 * include spaces, hyphens and apostrophes, so "mary-jane o'neil" -> "Mary-Jane O'Neil". Purely
 * presentational: the stored value is never changed, we only normalize however the user typed it.
 */
export function toNameCase(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .toLocaleLowerCase()
    .replace(/(^|[\s\-'’])(\p{L})/gu, (_match, sep: string, ch: string) => sep + ch.toLocaleUpperCase());
}

/** Joins a first/last pair as a display-cased full name: ("chaitanya","krishna") -> "Chaitanya Krishna". */
export function formatFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [toNameCase(firstName), toNameCase(lastName)].filter(Boolean).join(' ');
}

/**
 * Resolves a stored-file URL (resume, profile photo — always root-relative, e.g. `/files/...`,
 * per `IFileStorage`) against the gateway origin. The web app is served by its own nginx (no
 * `/files` proxy — docs/development/FrontendGuidelines.md §12.6), so a bare relative URL resolves against the
 * wrong origin and 404s through the SPA fallback. Absolute URLs (e.g. a future Azure Blob URL)
 * pass through unchanged.
 */
export function resolveFileUrl(url: string, gatewayUrl: string): string {
  return /^https?:\/\//i.test(url) ? url : `${gatewayUrl.replace(/\/$/, '')}${url}`;
}
